const INT_NUMBER_REGEX = /(\d+)/g;

const BRAZIL_AREA_CODE_LENGTH = 2;
const BRAZIL_PREFIX_PHONE_LENGTH = 5;

export function integerNumberFormatter(value: string): string {
  const matchedValue = value.match(INT_NUMBER_REGEX);

  return matchedValue ? matchedValue[0] : '';
}

// Strips every non-digit character, keeping only 0-9. Used as a mask that
// forces numeric-only inputs (e.g. the SisProUni inscription number).
export function onlyDigits(value: string): string {
  return (value || '').replace(/\D/g, '');
}

export function formatCpf(cpf: string): string {
  const matchedCpf = cpf.replace(/\.|-/g, '').match(/(\d{1,11})/g);
  const cpfValue = matchedCpf ? matchedCpf[0] : '';

  const cpfValueLength = cpfValue.length;
  let formattedCpfValue: string;

  if (cpfValueLength <= 3) {
    formattedCpfValue = cpfValue.replace(/(\d{1,3})/g, '$1');
  } else if (cpfValueLength <= 6) {
    formattedCpfValue = cpfValue.replace(/(\d{1,3})(\d{1,3})/g, '$1.$2');
  } else if (cpfValueLength <= 9) {
    formattedCpfValue = cpfValue.replace(/(\d{1,3})(\d{1,3})(\d{1,3})/g, '$1.$2.$3');
  } else {
    formattedCpfValue = cpfValue.replace(/(\d{1,3})(\d{1,3})(\d{1,3})(\d{1,2})/g, '$1.$2.$3-$4');
  }

  return formattedCpfValue;
}

export function formatDateString(date: string): string {
  const matchedDate = date.replace(/\//g, '').match(/(\d{1,8})/g);
  const dateValue = matchedDate ? matchedDate[0] : '';

  const dateValueLength = dateValue.length;
  let formattedDateValue: string;

  if (dateValueLength <= 2) {
    formattedDateValue = dateValue.replace(/(\d{1,2})/g, '$1');
  } else if (dateValueLength <= 4) {
    formattedDateValue = dateValue.replace(/(\d{1,2})(\d{1,2})/g, '$1/$2');
  } else {
    formattedDateValue = dateValue.replace(/(\d{1,2})(\d{1,2})(\d{1,4})/g, '$1/$2/$3');
  }

  return formattedDateValue;
}

export function formatPhoneNumber(phone: string) {
  const matchedPhone = phone.replace(/-| |\(|\)/g, '').match(/(\d{1,11})/g);
  const phoneValue = matchedPhone ? matchedPhone[0] : '';

  const phoneValueLength = phoneValue.length;
  let formattedPhoneValue: string;

  if (phoneValueLength <= BRAZIL_AREA_CODE_LENGTH) {
    formattedPhoneValue = phoneValue.replace(/(\d{1,2})/g, '($1');
  } else if (phoneValueLength <= BRAZIL_AREA_CODE_LENGTH + BRAZIL_PREFIX_PHONE_LENGTH) {
    formattedPhoneValue = phoneValue.replace(/(\d{1,2})(\d{1,5})/g, '($1) $2');
  } else {
    formattedPhoneValue = phoneValue.replace(/(\d{1,2})(\d{1,5})(\d{1,4})/g, '($1) $2-$3');
  }

  return formattedPhoneValue;
}

export function formatZipCode(zipCode: string) {
  const matchedZipCode = zipCode.replace(/[.-]/g, '').match(/(\d{1,8})/g);
  const zipCodeValue = matchedZipCode ? matchedZipCode[0] : '';
  const zipCodeLength = zipCodeValue.length;
  let formattedZipCode: string;

  if (zipCodeLength <= 2) {
    formattedZipCode = zipCodeValue.replace(/(\d{1,2})/g, '$1');
  } else if (zipCodeLength <= 5) {
    formattedZipCode = zipCodeValue.replace(/(\d{1,2})(\d{1,3})/g, '$1.$2');
  } else {
    formattedZipCode = zipCodeValue.replace(/(\d{1,2})(\d{1,3})(\d{1,3})/g, '$1.$2-$3');
  }

  return formattedZipCode;
}

export function formatStringToDate(dateString: string | undefined): Date | undefined {
  if (!dateString || dateString.split('/').length !== 3) return undefined;

  const [day, month, year] = dateString.split('/');
  return new Date(Number(year), Number(month) - 1, Number(day));
}

export function formatCreditCardNumber(creditCardNumber: string) {
  const matchedCreditCardNumber = creditCardNumber.replace(/\s/g, '').match(/(\d{1,16})/g);
  const creditCardValue = matchedCreditCardNumber ? matchedCreditCardNumber[0] : '';
  const creditCardLength = creditCardValue.length;
  let formattedCreditCardNumber: string;

  if (creditCardLength <= 4) {
    formattedCreditCardNumber = creditCardValue.replace(/(\d{1,4})/g, '$1');
  } else if (creditCardLength <= 8) {
    formattedCreditCardNumber = creditCardValue.replace(/(\d{1,4})(\d{1,4})/g, '$1 $2');
  } else if (creditCardLength <= 12) {
    formattedCreditCardNumber = creditCardValue.replace(/(\d{1,4})(\d{1,4})(\d{1,4})/g, '$1 $2 $3');
  } else {
    formattedCreditCardNumber = creditCardValue.replace(
      /(\d{1,4})(\d{1,4})(\d{1,4})(\d{1,4})/g,
      '$1 $2 $3 $4',
    );
  }

  return formattedCreditCardNumber;
}

export function formatCVV(cvvNumber: string) {
  const matchedCVV = cvvNumber.replace(/\s/g, '').match(/(\d{1,4})/g);
  const cvvValue = matchedCVV ? matchedCVV[0] : '';
  const formattedCVV = cvvValue.replace(/(\d{1,4})/g, '$1');

  return formattedCVV;
}

export function formatStringToTitleCase(words: string) {
  return words
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function formatSideWhitespaces(word: string) {
  return word.trim();
}

// Converts an ISO date ("2000-05-20") to the BR masked format ("20/05/2000").
// Leaves already-BR or empty values untouched.
export function isoToBrDate(value?: string): string {
  if (!value) return '';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

// Converts a BR masked date ("20/05/2000") to ISO ("2000-05-20") for storage.
// Leaves incomplete or empty values untouched.
export function brDateToIso(value?: string): string {
  if (!value) return '';
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
}

// Máscara de moeda BRL "estilo centavos": os dígitos preenchem da direita para
// a esquerda. Ex.: "150000" -> "R$ 1.500,00"; "1500" -> "R$ 15,00".
export function formatCurrency(value: string): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  const amount = (Number(digits) / 100).toFixed(2);
  const [intPart, decimals] = amount.split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${withThousands},${decimals}`;
}

// Converte um número (reais) para a string mascarada. Ex.: 1500 -> "R$ 1.500,00".
export function formatNumberToCurrency(value: number): string {
  if (value == null || Number.isNaN(value)) return '';
  return formatCurrency(String(Math.round(value * 100)));
}

// Converte a string mascarada de volta para número. Ex.: "R$ 1.500,00" -> 1500.
export function parseCurrencyToNumber(value: string): number {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits ? Number(digits) / 100 : 0;
}

export function formatWesterosError(
  errorMessage: string,
): { errorDescription: string; errorCode: number } {
  const errorDescriptionPosition = errorMessage.indexOf('Error description: ');
  const errorCodePosition = errorMessage.indexOf('Error code: ');
  const endErrorDescriptionPosition = errorMessage.indexOf('\n', errorDescriptionPosition);
  const endErrorCodePosition = errorMessage.indexOf('\n', errorCodePosition);

  const errorDescription = errorMessage.slice(
    errorDescriptionPosition + 'Error description: '.length,
    endErrorDescriptionPosition,
  );

  const errorCode = +errorMessage.slice(
    errorCodePosition + 'Error code: '.length,
    endErrorCodePosition,
  );

  return { errorDescription, errorCode };
}
